import { getStorageAt, multicall, simulateContract, writeContract } from '@wagmi/core'
import { ConfigBuilderAbi } from 'abis/ConfigBuilderAbi'
import { HomechainOmnichainMessengerAbi } from 'abis/HomechainOmnichainMessagerAbi'
import { IndexV2Abi } from 'abis/IndexV2'
import { VaultViewerAbi } from 'abis/VaultViewerAbi'
import { WAD } from 'app-constants'
import Big from 'big.js'
import { notifyTransactionError } from 'components'
import type { SuccessPayload } from 'components/widgets/invest-component/storage/invest-states'
import _ from 'lodash'
import { defaultGasMultiplier, gasMultiplier } from 'services/query-v2/deposit-redeem-v1'
import type { TransactionResult } from 'services/query-v2/deposit-redeem-v1/utils'
import { viemPublicClients, wagmiConfig } from 'services/wallet/chains'
import { baseCurrencies, convertAmountToWei } from 'utils'
import {
  type Address,
  type Hex,
  type PublicClient,
  encodeAbiParameters,
  encodeFunctionData,
  encodePacked,
  isAddressEqual,
  keccak256,
  padHex,
  parseAbiParameters,
  toHex,
  zeroAddress,
} from 'viem'
import { estimateContractGas } from 'viem/actions'
import { zetachain } from 'viem/chains'
import type { ParsedConfigs } from '..'
import { ZeroExAggregator } from '../aggregators/0x-aggregators'
import { bridgingCurrencies, getBridging } from '../bridging-currencies'
import { additionalRemoteContracts, deployments } from '../deployments'
import { swap } from '../pendle-utils'
import { getRedstonePayload } from '../redstone-utils'
import { createAirdropOptions, fromA160U96, toChainId } from '../utils'
import { BridgeParamsAbi } from './additional-abis/bridge-params'
import { EscrowHelperAbi } from './additional-abis/escrow-helpers'
import { LzHelperAbi } from './additional-abis/lz-helper'
import { StargateRouterAbi } from './additional-abis/sg-helper'
import { MessageCallbackAbi } from './additional-abis/trades'
import { HOMECHAIN_CALLBACK_BUILDER, REMOTE_CALLBACK_BUILDER } from './callback-helpers'
import { escrowHelper, vaultViewers } from './configs'
import type { Batch, BatchData, Target, Trade } from './interfaces'
import { LZ_HELPER, estimateLzFees, lzLookup } from './lz-helpers'
import { sumBalancesByCurrency, zeroTarget } from './utils'

export const redeemV2 = async (
  client: PublicClient,
  configs: ParsedConfigs,
  props: SuccessPayload,
) => {
  console.log('index-v2-redeem')

  const {
    inputCurrency: { chainId: homeChainId },
    clearState,
  } = props

  try {
    let result: TransactionResult
    switch (homeChainId) {
      case zetachain.id: {
        result = await redeemV2Zeta(client, configs, props)
        break
      }
      default: {
        result = await redeemV2Core(client, configs, props)
        break
      }
    }

    if (clearState) clearState()

    return result
  } catch (e) {
    console.error(e)
    notifyTransactionError()
  }
}

async function redeemV2Core(
  client: PublicClient,
  configs: ParsedConfigs,
  props: SuccessPayload,
): Promise<TransactionResult> {
  const {
    inputCurrency,
    account,
    sellAmount,
    outputCurrency,
    productAddress,
    stopProcessing,
    isUserContract,
  } = props

  const chainIds = Object.keys(configs.assets).map(Number)

  const homeChain = inputCurrency.chainId

  const homeClient = viemPublicClients[homeChain]
  const reserveAddress = configs.reserveAsset

  const {
    EscrowDeployer,
    DefaultEscrow,
    ConfigBuilder,
    Messenger: HomeMessenger,
  } = deployments[homeChain][productAddress]

  const contract = {
    address: ConfigBuilder,
    abi: ConfigBuilderAbi,
  }

  const [[, redeemConfig], { staticPriceOracle }] = await multicall(wagmiConfig, {
    contracts: [
      {
        ...contract,
        functionName: 'configs',
        args: [configs.config],
      },
      {
        ...contract,
        functionName: 'getState',
      },
    ],
    allowFailure: false,
  })

  // const assetsPrices = await getPricesStore()
  //TODO
  const amount = BigInt(convertAmountToWei(sellAmount, inputCurrency.decimals).toFixed())
  const aggregator = ZeroExAggregator.builder.build()

  const redeemParams = {
    config: redeemConfig,
    owner: account,
    shares: amount,
    payload: await getRedstonePayload.fromContract(staticPriceOracle),
  } as const

  const { result } = await simulateContract(wagmiConfig, {
    account: HomeMessenger,
    address: productAddress,
    abi: IndexV2Abi,
    functionName: 'redeem',
    args: [redeemParams, account, account],
  })

  const { activeChainIds } = await homeClient.readContract({
    address: ConfigBuilder,
    abi: ConfigBuilderAbi,
    functionName: 'getState',
  })

  const [homeChainId, ...remoteChainIds] = activeChainIds.map((c) => toChainId(c, chainIds))

  const ranges = []
  const homeTrades = []

  const remoteKSets: bigint[] = []

  const [homeBalances, homeEscrow, escrowDeployed] = await homeClient.readContract({
    address: escrowHelper[homeChainId],
    abi: EscrowHelperAbi,
    functionName: 'userBalancesOf',
    args: [
      account,
      EscrowDeployer,
      productAddress,
      [...new Set(remoteChainIds.map((c) => getBridging(homeChainId, c)))],
    ],
  })

  const homeCurrencyAndBalances = homeBalances.currencies.map((currency, i) => ({
    currency: currency.toLowerCase(),
    balance: homeBalances.currencyBalances[i],
  }))

  const netAmount = amount - (amount * BigInt(configs.config.redemptionFee.BPs)) / 10000n

  // k only
  {
    ranges.push({ start: 0n, end: 0n })
    let k = result.accountKRedeemed
    if (result.accountReserveRedeemed !== 0n) {
      const kPerShare =
        (result.totalKBeforeRedeem * WAD) /
        (result.totalSupplyAfterAUMAccrual - result.totalReserveShares)
      k = (kPerShare * netAmount) / WAD
    }

    remoteKSets.push(k)
    const balancesSet1 = redeemConfig.homeCurrencies.map((c) => {
      const { address: currency, value } = fromA160U96(c)

      const amount = (value * k) / WAD
      return { currency: currency.toLowerCase(), balance: amount }
    })

    homeTrades.push(
      (
        await Promise.all(
          sumBalancesByCurrency([...balancesSet1, ...homeCurrencyAndBalances])
            .filter(
              ({ currency, balance }) =>
                !isAddressEqual(currency, outputCurrency.address) && balance !== 0n,
            )
            .map(async ({ currency, balance }) =>
              swap(wagmiConfig, currency, balance, outputCurrency.address, homeEscrow, account),
            ),
        )
      ).flat(),
    )
  }

  // k + reserve
  if (result.accountReserveSharesRedeemed !== 0n && result.accountKRedeemed !== 0n) {
    const diff = result.accountReserveSharesRedeemed / 100n

    ranges.push({
      start: result.accountReserveSharesRedeemed - diff,
      end: result.accountReserveSharesRedeemed + diff,
    })
    remoteKSets.push(result.accountKRedeemed)

    const balancesSet2 = redeemConfig.homeCurrencies.map((c, i) => {
      const { address: currency, value } = fromA160U96(c)

      const amount =
        i === 0
          ? (value * result.accountKRedeemed) / WAD + result.accountReserveRedeemed
          : (value * result.accountKRedeemed) / WAD
      return { currency: currency.toLowerCase(), balance: amount }
    })

    homeTrades.push(
      (
        await Promise.all(
          sumBalancesByCurrency([...balancesSet2, ...homeCurrencyAndBalances])
            .filter(
              ({ currency, balance }) =>
                !isAddressEqual(currency, outputCurrency.address) && balance !== 0n,
            )
            .map(async ({ currency, balance }) =>
              swap(wagmiConfig, currency, balance, outputCurrency.address, homeEscrow, account),
            ),
        )
      ).flat(),
    )
  }

  // reserve only
  if (!isAddressEqual(outputCurrency.address, reserveAddress)) {
    ranges.push({ start: netAmount, end: amount })

    let balance: bigint
    if (result.accountKRedeemed === 0n) {
      balance = result.accountReserveRedeemed
    } else {
      if (result.totalReserveShares === 0n) {
        const valueInBase = (netAmount * result.totalValuation) / result.totalSupplyAfterAUMAccrual
        balance =
          (valueInBase * result.reservePriceInQ128) / BigInt('0x100000000000000000000000000000000')
      } else {
        const reservePerShare =
          (result.accountReserveRedeemed * WAD) / result.accountReserveSharesRedeemed
        balance = (reservePerShare * netAmount) / WAD
      }
    }

    homeTrades.push(
      (
        await Promise.all(
          sumBalancesByCurrency([{ currency: reserveAddress, balance }, ...homeCurrencyAndBalances])
            .filter(
              ({ currency, balance }) =>
                !isAddressEqual(currency, outputCurrency.address) && balance !== 0n,
            )
            .map(async ({ currency, balance }) =>
              swap(wagmiConfig, currency, balance, outputCurrency.address, homeEscrow, account),
            ),
        )
      ).flat(),
    )
  }
  // else branch
  ranges.push({ start: 0n, end: 0n })
  homeTrades.push([])

  const { eid: homeEid } = lzLookup[homeChainId]

  const batches: Batch[] = []

  for (const [chainIndex, dstChainId] of remoteChainIds.entries()) {
    // if (!isChainId(dstChainId)) throw `Invalid chainId: ${dstChainId}`;
    const { Vault, Messenger: RemoteMessenger } =
      additionalRemoteContracts[productAddress][dstChainId]

    const remoteClient = viemPublicClients[dstChainId]

    const bridgingCurrency = getBridging(dstChainId, homeChainId)

    const [{ currencies, currencyBalances, snapshots, snapshotBalances }, escrow] =
      await remoteClient.readContract({
        address: escrowHelper[dstChainId],
        abi: EscrowHelperAbi,
        functionName: 'userBalancesOf',
        args: [account, EscrowDeployer, Vault, []],
      })

    const currencyAndBalances = currencies.map((currency, i) => ({
      currency: currency.toLowerCase(),
      balance: currencyBalances[i],
    }))

    const additionalWithdrawals: Trade[] = snapshots.map((snapshot, i) => ({
      skipRevert: true,
      currency: bridgingCurrency,
      allowanceTarget: zeroAddress,
      addr: Vault,
      value: 0n,
      data: encodeFunctionData({
        abi: IndexV2Abi,
        functionName: 'withdraw',
        args: [snapshot, snapshotBalances[i], escrow],
      }),
    }))

    const [snapshot] = await remoteClient.readContract({
      address: vaultViewers[dstChainId],
      abi: VaultViewerAbi,
      functionName: 'stateInfo',
      args: [Vault],
    })

    const [currenciesOfSnapshot] = await remoteClient.readContract({
      address: vaultViewers[dstChainId],
      abi: VaultViewerAbi,
      functionName: 'snapshotInfo',
      args: [Vault, BigInt(snapshot)],
    })

    const batchData: BatchData[] = []
    for (const k of remoteKSets) {
      const allCurrenciesAndBalances = [
        ...currencyAndBalances,
        ...currenciesOfSnapshot.map((c) => {
          const { address, value } = fromA160U96(c)
          return {
            currency: address.toLowerCase(),
            balance: (value * k) / WAD,
          }
        }),
      ]

      const tradeInputs = _(allCurrenciesAndBalances)
        .groupBy('currency')
        .map((inputs, currency) => ({
          currency: currency as Address,
          balance: BigInt(_.sumBy(inputs, 'balance')),
        }))
        .value()

      const trades: Trade[] = []
      for (const { currency, balance } of tradeInputs) {
        if (isAddressEqual(currency, bridgingCurrency) || balance === 0n) continue

        const quote = await aggregator.quote(
          dstChainId,
          currency,
          bridgingCurrency,
          (balance * 101n) / 100n,
          escrow,
        )

        trades.push({
          skipRevert: true,
          currency,
          allowanceTarget: quote.allowanceTarget,
          addr: quote.to,
          value: quote.value,
          data: quote.data,
        })
      }

      const shouldBridge = trades.length !== 0

      const callbacks = [
        encodeAbiParameters(MessageCallbackAbi, [
          [...additionalWithdrawals, ...trades],
          zeroTarget,
        ]),
      ]

      const bridgingEstimationData = [
        encodeAbiParameters(parseAbiParameters('uint8, address, uint256, bytes'), [
          3,
          account,
          k,
          callbacks[0],
        ]),
      ]

      const { result: bridgingAmount } = await remoteClient.simulateContract({
        address: escrowHelper[dstChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateBridgingAmount',
        args: [RemoteMessenger, bridgingEstimationData, escrow, bridgingCurrency],
        stateOverride: [
          {
            address: RemoteMessenger,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[dstChainId], { size: 32 }),
              },
            ],
          },
        ],
      })

      const homeBridgingCurrency = getBridging(homeChainId, dstChainId)
      const quote = isAddressEqual(homeBridgingCurrency, outputCurrency.address)
        ? {
            to: zeroAddress,
            data: '0x' as const,
            value: 0n,
            allowanceTarget: zeroAddress,
          }
        : await aggregator.quote(
            homeChainId,
            homeBridgingCurrency,
            outputCurrency.address,
            (bridgingAmount * 101n) / 100n,
            homeEscrow,
          )

      const homeTradesBridging: Trade[] = [
        {
          skipRevert: false,
          currency: homeBridgingCurrency,
          allowanceTarget: quote.allowanceTarget,
          addr: quote.to,
          value: quote.value,
          data: quote.data,
        },
      ]

      const homeTradesCallback: Target = {
        skipRevert: true,
        addr: HOMECHAIN_CALLBACK_BUILDER[homeChainId],
        value: 0n,
        data: encodeAbiParameters(parseAbiParameters('address, address'), [
          outputCurrency.address,
          account,
        ]),
      }

      let homechainCallbacksData = encodeAbiParameters(MessageCallbackAbi, [
        homeTradesBridging,
        homeTradesCallback,
      ])

      let { result: homeGas } = await homeClient.simulateContract({
        address: escrowHelper[homeChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateHomeMessageGas',
        args: [
          escrowDeployed ? account : zeroAddress,
          EscrowDeployer,
          homeBridgingCurrency,
          bridgingAmount,
          homechainCallbacksData,
          3n,
        ],
        stateOverride: [
          {
            address: escrowDeployed ? escrow : DefaultEscrow,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[homeChainId], { size: 32 }),
              },
            ],
          },
          {
            address: homeBridgingCurrency,
            stateDiff: [
              {
                slot: keccak256(
                  encodeAbiParameters(parseAbiParameters('address user, uint slot'), [
                    escrowDeployed ? escrow : DefaultEscrow,
                    isAddressEqual(homeBridgingCurrency, baseCurrencies.USDbC.address)
                      ? BigInt(52)
                      : BigInt(51),
                  ]),
                ),
                value: padHex(toHex(bridgingAmount), { size: 32 }),
              },
            ],
          },
        ],
      })

      homeGas = (homeGas * 110n) / 100n

      homeTradesBridging[0].skipRevert = true
      homechainCallbacksData = encodeAbiParameters(MessageCallbackAbi, [
        homeTradesBridging,
        homeTradesCallback,
      ])

      const lzFees = await remoteClient.readContract({
        address: lzLookup[dstChainId].sgComposer,
        abi: StargateRouterAbi,
        functionName: 'quoteLayerZeroFee',
        args: [
          homeEid,
          1,
          HomeMessenger,
          homechainCallbacksData,
          {
            dstGasForCall: homeGas,
            dstNativeAmount: 0n,
            dstNativeAddr: HomeMessenger,
          },
        ],
      })

      const remoteAirdropAmount = (lzFees[0] * 110n) / 100n

      const [airdropAmountInNative] = await homeClient.readContract({
        address: LZ_HELPER[homeChainId],
        abi: LzHelperAbi,
        functionName: 'convertToDstNative',
        args: [HomeMessenger, [remoteAirdropAmount], [lzLookup[dstChainId].eid]],
      })

      const bridgeParams = {
        currency: bridgingCurrency,
        sgRouter: lzLookup[dstChainId].sgComposer,
        refundAddress: escrow,
        srcPoolId: BigInt(bridgingCurrencies[dstChainId].poolId),
        dstPoolId: BigInt(bridgingCurrencies[dstChainId].poolId),
        homeGas,
        packedRecipient: encodePacked(['address'], [escrow]),
        payload: homechainCallbacksData,
        dstEid: homeEid,
        maxSlippageBP: 300,
      }

      const target: Target = {
        skipRevert: true,
        addr: REMOTE_CALLBACK_BUILDER[dstChainId],
        value: 0n,
        data: encodeAbiParameters([BridgeParamsAbi], [bridgeParams]),
      }
      callbacks[0] = encodeAbiParameters(MessageCallbackAbi, [
        [...additionalWithdrawals, ...trades],
        target,
      ])

      const estimationData = callbacks.map((c, i) => {
        if (i === 0) {
          return encodeAbiParameters(parseAbiParameters('uint8, address, uint256, bytes'), [
            3,
            account,
            k,
            c,
          ])
        }
        return encodeAbiParameters(parseAbiParameters('uint8, address, bytes'), [2, account, c])
      })

      const currentNativeBalance =
        currencyAndBalances.find((c) => isAddressEqual(c.currency as Hex, zeroAddress))?.balance ??
        0n

      let { result: callbacksGas } = await remoteClient.simulateContract({
        address: escrowHelper[dstChainId],
        abi: EscrowHelperAbi,
        functionName: 'estimateMessagesGas',
        args: [RemoteMessenger, estimationData],
        stateOverride: [
          {
            address: RemoteMessenger,
            stateDiff: [
              {
                slot: toHex(1, { size: 32 }),
                value: padHex(escrowHelper[dstChainId], { size: 32 }),
              },
            ],
          },
          {
            address: escrow,
            balance: currentNativeBalance + remoteAirdropAmount,
          },
        ],
      })

      callbacksGas = callbacksGas.map((c) => (c * 110n) / 100n)

      let chainValue = 0n
      const airdropAmount = shouldBridge ? remoteAirdropAmount : 0n
      await Promise.all(
        callbacks.map(async (callback, i) => {
          const [fee] = await estimateLzFees({
            client: homeClient,
            dstChainId,
            uaContractAddress: HomeMessenger,
            payload: callback,
            options: createAirdropOptions(
              callbacksGas[i],
              i === callbacks.length - 1 ? airdropAmount : 0n,
              escrow,
            ),
          })

          chainValue += fee
        }),
      )

      chainValue += shouldBridge ? airdropAmountInNative : 0n

      batchData.push({
        callbacks,
        additionalGas: callbacksGas.map((g) =>
          g > BigInt(configs.lzConfig.minGas[chainIndex])
            ? g - BigInt(configs.lzConfig.minGas[chainIndex])
            : 0n,
        ),
        airdropAmount,
        chainValue,
      })
    }

    batches.push({
      escrow,
      data: batchData,
    })
  }

  const value = remoteKSets
    .map((_, i) => batches.reduce<bigint>((result, b) => result + b.data[i].chainValue, 0n))
    .reduce((max, current) => {
      return current > max ? current : max
    }, 0n)

  const sendParams = {
    config: {
      eIds: BigInt(configs.lzConfig.eIds),
      minGas: configs.lzConfig.minGas.map(BigInt),
    },
    ranges,
    batches,
    trades: homeTrades,
    escrowCallback: {
      skipRevert: true,
      addr: HOMECHAIN_CALLBACK_BUILDER[homeChainId],
      value: 0n,
      data: encodeAbiParameters(parseAbiParameters('address, address'), [
        outputCurrency.address,
        account,
      ]),
    },
    zroPaymentAddress: zeroAddress,
    packedRecipient: encodePacked(['address'], [account]),
  }

  if (stopProcessing) stopProcessing()
  const { request } = await simulateContract(wagmiConfig, {
    account,
    address: HomeMessenger,
    abi: HomechainOmnichainMessengerAbi,
    functionName: 'redeem',
    value,
    args: [redeemParams, sendParams],
    stateOverride: [
      {
        address: account,
        balance: value,
      },
    ],
  })

  if (result.totalReserveShares !== 0n && result.accountKRedeemed === 0n) {
    const data = await getStorageAt(wagmiConfig, {
      address: productAddress,
      slot: toHex(18),
    })

    if (!data) throw 'Slot not found'

    const mask128 = (1n << 128n) - 1n
    const totalSupply = (BigInt(data) & mask128) - result.totalReserveShares

    const otherData = BigInt(data) >> 128n
    const newSlotValue = (otherData << 128n) | totalSupply
    console.log('newSlotValue: ', newSlotValue)
    // TODO: FIX gas estimations
    const gas = await estimateContractGas(homeClient, {
      account,
      address: HomeMessenger,
      abi: HomechainOmnichainMessengerAbi,
      functionName: 'redeem',
      value,
      args: [redeemParams, sendParams],
      stateOverride: [
        {
          address: account,
          balance: value,
        },
        {
          address: productAddress,
          stateDiff: [
            {
              slot: toHex(18, { size: 32 }),
              value: toHex(newSlotValue),
            },
            {
              slot: toHex(19, { size: 32 }),
              value: padHex(zeroAddress, { size: 32 }),
            },
          ],
        },
      ],
    })
    console.log('gas 1: ', gas)
    console.log(
      'scaled gas 1: ',
      (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000),
    )

    request.gas =
      (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)
  } else {
    if (request.gas) {
      request.gas =
        (request.gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) /
        BigInt(10000)
      console.log('gas 2: ', request.gas)
      console.log(
        'scaled gas 2: ',
        (request.gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) /
          BigInt(10000),
      )
    } else {
      const gas = await estimateContractGas(homeClient, {
        account,
        address: HomeMessenger,
        abi: HomechainOmnichainMessengerAbi,
        functionName: 'redeem',
        value,
        args: [redeemParams, sendParams],
        stateOverride: [
          {
            address: account,
            balance: value,
          },
        ],
      })
      console.log('gas 3: ', gas)
      console.log(
        'scaled gas 3: ',
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000),
      )

      request.gas =
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)
    }
  }

  const hash = await writeContract(wagmiConfig, request)

  return {
    hash,
    params: props,
    buyAmount: Big(sellAmount.toFixed(3)),
    chains: chainIds.filter((chain) => chain !== homeChain).map((chain) => lzLookup[chain].eid),
    isSingleChain: false,
    isUserContract,
  }
}

async function redeemV2Zeta(
  homeClient: PublicClient,
  configs: ParsedConfigs,
  props: SuccessPayload,
): Promise<TransactionResult> {
  const {
    inputCurrency,
    account,
    sellAmount,
    outputCurrency,
    productAddress,
    stopProcessing,
    isUserContract,
  } = props

  const homeChainId = inputCurrency.chainId
  const chainIds = Object.keys(configs.assets).map(Number)

  const { ConfigBuilder, Messenger: HomeMessenger } = deployments[homeChainId][productAddress]

  const configBuilder = {
    address: ConfigBuilder,
    abi: ConfigBuilderAbi,
  }

  const [[, redeemConfig], { staticPriceOracle }] = await multicall(wagmiConfig, {
    contracts: [
      {
        ...configBuilder,
        functionName: 'configs',
        args: [configs.config],
      },
      {
        ...configBuilder,
        functionName: 'getState',
      },
    ],
    allowFailure: false,
  })

  const amount = BigInt(convertAmountToWei(sellAmount, inputCurrency.decimals).toFixed())

  const redeemParams = {
    config: redeemConfig,
    owner: account,
    shares: amount,
    payload: await getRedstonePayload.fromContract(staticPriceOracle),
  } as const

  const { result } = await simulateContract(wagmiConfig, {
    account: HomeMessenger,
    address: productAddress,
    abi: IndexV2Abi,
    functionName: 'redeem',
    args: [redeemParams, account, account],
  })
  console.log('result: ', result)

  const sendParams = {
    config: {
      eIds: BigInt(configs.lzConfig.eIds),
      minGas: configs.lzConfig.minGas.map(BigInt),
    },
    ranges: [{ start: 0n, end: 0n }],
    batches: [],
    trades: [[]],
    escrowCallback: {
      skipRevert: true,
      addr: HOMECHAIN_CALLBACK_BUILDER[homeChainId],
      value: 0n,
      data: encodeAbiParameters(parseAbiParameters('address, address'), [
        outputCurrency.address,
        account,
      ]),
    },
    zroPaymentAddress: zeroAddress,
    packedRecipient: encodePacked(['address'], [account]),
  }

  if (stopProcessing) stopProcessing()

  const { request } = await simulateContract(wagmiConfig, {
    account,
    address: HomeMessenger,
    abi: HomechainOmnichainMessengerAbi,
    functionName: 'redeem',
    args: [redeemParams, sendParams],
  })

  if (result.totalReserveShares !== 0n && result.accountKRedeemed === 0n) {
    const gas = await estimateContractGas(homeClient, {
      account,
      address: HomeMessenger,
      abi: HomechainOmnichainMessengerAbi,
      functionName: 'redeem',
      args: [redeemParams, sendParams],
    })
    console.log('gas 1: ', gas)
    console.log(
      'scaled gas 1: ',
      (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000),
    )

    request.gas =
      (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)
  } else {
    if (request.gas) {
      request.gas =
        (request.gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) /
        BigInt(10000)
      console.log('gas 2: ', request.gas)
      console.log(
        'scaled gas 2: ',
        (request.gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) /
          BigInt(10000),
      )
    } else {
      const gas = await estimateContractGas(homeClient, {
        account,
        address: HomeMessenger,
        abi: HomechainOmnichainMessengerAbi,
        functionName: 'redeem',
        args: [redeemParams, sendParams],
      })
      console.log('gas 3: ', gas)
      console.log(
        'scaled gas 3: ',
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000),
      )

      request.gas =
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)
    }
  }

  const hash = await writeContract(wagmiConfig, request)

  return {
    hash,
    params: props,
    buyAmount: Big(sellAmount.toFixed(3)),
    chains: chainIds.filter((chain) => chain !== homeChainId).map((chain) => lzLookup[chain].eid),
    isSingleChain: false,
    isUserContract,
  }
}
